import { resolveEffectiveThreshold } from "@capture/shared";
import { usePaneStore } from "../state/store-context";
import {
  useSmartSkipIterator,
  jobTypeThresholdAsTypeFloors,
} from "../hooks/useSmartSkipIterator";

interface StatusBarProps {
  onReject: () => void;
  onHold: () => void;
  onShowShortcuts?: () => void;
  onFlagMisidentified?: () => void;
}

export function StatusBar({ onReject, onHold, onShowShortcuts, onFlagMisidentified }: StatusBarProps) {
  const fields = usePaneStore((s) => s.fields);
  const classification = usePaneStore((s) => s.classification);
  const docIndex = usePaneStore((s) => s.docIndex);
  const totalDocs = usePaneStore((s) => s.totalDocs);
  const currentPageIndex = usePaneStore((s) => s.currentPageIndex);
  const pages = usePaneStore((s) => s.pages);
  const focusMode = usePaneStore((s) => s.focusMode);
  const verifyThreshold = usePaneStore((s) => s.verifyThreshold);
  // Handwriting Phase 3 — surface the Ctrl+E hint ONLY while the active field
  // actually has engine candidates (discoverability without noise; absent =
  // gate off / agreement, where the swap key is a no-op).
  const activeHasCandidates = usePaneStore(
    (s) => s.fields[s.activeFieldIndex]?.handwritingCandidates != null,
  );

  // Phase 1.5 WU-4 (R-135) — counter aligned with the smart-skip iterator
  // visit set (all fields below effective floor), not the legacy
  // per-field `needsAttention` flag which was only set on the OCR subset.
  // F-046 — the counter honors the step's re-verification policy so a
  // supervisor doc whose fields were verified upstream doesn't show "N need
  // attention" with everything green.
  const stepReverifyPolicy = usePaneStore((s) => s.stepReverifyPolicy);
  const typeFloors = jobTypeThresholdAsTypeFloors(fields, verifyThreshold);
  const visit = useSmartSkipIterator(fields, typeFloors, stepReverifyPolicy);
  const attentionCount = visit.visitOrder.length;
  const doneCount = fields.filter(
    (f) => f.validationStatus === "pass" || f.validationStatus === "overridden",
  ).length;
  const autoVerifiedCount = fields.filter(
    (f) =>
      f.ocrConfidence !== null &&
      // F-CAPTURE-044 — the station's own threshold (field -> job type -> default).
      f.ocrConfidence >= resolveEffectiveThreshold(f.verifyThreshold, verifyThreshold) &&
      !f.isDirty &&
      f.validationStatus !== "fail",
  ).length;

  return (
    <div className="status-bar">
      <div className="status-left">
        <span className="status-doc">
          Doc {docIndex}/{totalDocs}
        </span>
        {pages.length > 1 && (
          <span className="status-page">
            Page {currentPageIndex + 1}/{pages.length}
          </span>
        )}
        {classification && (
          <span className="status-class">{classification}</span>
        )}
      </div>
      <div className="status-center">
        {attentionCount > 0 ? (
          <span className="status-attention">
            {attentionCount} field{attentionCount !== 1 ? "s" : ""} need attention
          </span>
        ) : (
          <span className="status-ready">All fields ready</span>
        )}
        <span className="status-progress">
          {doneCount}/{fields.length}
        </span>
        {autoVerifiedCount > 0 && (
          <span className="status-auto-verified">
            {autoVerifiedCount} auto-verified
          </span>
        )}
        {focusMode && (
          <span className="status-focus-badge">FOCUS</span>
        )}
      </div>
      <div className="status-right">
        <span className="status-shortcut-hint" aria-hidden="true">
          <kbd>Tab</kbd> next · <kbd>Ctrl</kbd>+<kbd>Enter</kbd> accept · <kbd>Ctrl</kbd>+<kbd>;</kbd> zoom
          {activeHasCandidates && (
            <> · <kbd>Ctrl</kbd>+<kbd>E</kbd> swap</>
          )}
        </span>
        {onShowShortcuts && (
          <button
            className="status-btn shortcuts"
            onClick={onShowShortcuts}
            title="Keyboard shortcuts (?)"
            aria-label="Show keyboard shortcuts"
          >
            ?
          </button>
        )}
        <button className="status-btn reject" onClick={onReject} title="Ctrl+R">
          Reject
        </button>
        <button className="status-btn hold" onClick={onHold} title="Ctrl+H">
          Hold
        </button>
        {onFlagMisidentified && (
          <button className="status-btn misident" onClick={onFlagMisidentified} title="Flag wrong document type">
            Wrong type
          </button>
        )}
      </div>

      <style>{`
        .status-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-1) var(--space-3);
          background: var(--color-bg-secondary);
          border-top: 1px solid var(--color-border);
          font-size: 0.7rem;
          color: var(--color-text-muted);
        }
        .status-left, .status-center, .status-right {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }
        .status-class {
          padding: 1px 6px;
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-sm);
        }
        .status-attention { color: var(--color-warning); font-weight: 600; }
        .status-ready { color: var(--color-success); }
        .status-btn {
          padding: 2px 10px;
          font-size: 0.7rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: transparent;
          color: var(--color-text-secondary);
          cursor: pointer;
        }
        .status-btn:hover { background: var(--color-bg-tertiary); }
        .status-btn.reject:hover { border-color: var(--color-error); color: var(--color-error); }
        .status-auto-verified {
          color: var(--color-success);
          font-size: 0.65rem;
        }
        .status-focus-badge {
          padding: 0 4px;
          background: var(--color-primary);
          color: white;
          border-radius: var(--radius-sm);
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.05em;
        }
        .status-shortcut-hint {
          font-size: 0.65rem;
          color: var(--color-text-muted);
          margin-right: var(--space-2);
        }
        .status-shortcut-hint kbd {
          display: inline-block;
          padding: 0 4px;
          margin: 0 1px;
          font-family: inherit;
          font-size: 0.6rem;
          line-height: 1.4;
          background: var(--color-bg-tertiary);
          border: 1px solid var(--color-border);
          border-radius: 2px;
          color: var(--color-text-secondary);
        }
        .status-btn.shortcuts {
          padding: 0 6px;
          font-weight: 600;
          min-width: 20px;
        }
        @media (max-width: 900px) {
          .status-shortcut-hint { display: none; }
        }
      `}</style>
    </div>
  );
}
